import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, SkipForward, Volume2, VolumeX, HelpCircle, Settings } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { CycleDots } from "../components/CycleDots";
import { PrayerCard } from "../components/PrayerCard";
import { PomodoroRing } from "../components/PomodoroRing";
import { OnboardingTour } from "../components/OnboardingTour";
import { toast } from "sonner";

const TUTORIAL_STORAGE_KEY = "pomodoro_cristao_tutorial_v1";
const DURATIONS_STORAGE_KEY = "pomodoro_cristao_durations_v1";

const TUTORIAL_STEPS = [
  {
    title: "O que é o método Pomodoro?",
    content:
      "Já sentou pra estudar e, minutos depois, percebeu a mente vagando? O Pomodoro ataca exatamente isso. Você escolhe uma tarefa — digamos, aquele capítulo de história — e foca só nela por um tempo curto, sem celular por perto. Quando o tempo acaba, você para e descansa alguns minutos antes do próximo bloco. Repetindo esse ritmo, a mente aprende a manter o foco sem se esgotar. O nome vem do cronômetro de cozinha em formato de tomate que o criador da técnica, Francesco Cirillo, usava nos anos 80.",
  },
  {
    title: "Bem-vindo(a) ao Pomodoro Cristão",
    content:
      "Aqui, cada pausa também é um convite à oração, unindo produtividade e vida espiritual. Vamos conhecer a tela em poucos passos?",
  },
  {
    target: '[data-testid="mode-tabs"]',
    title: "Modos do ciclo",
    content:
      "Alterne entre Estudo (25 min), Pausa curta (5 min) e Pausa longa (15 min), que chega automaticamente a cada 4 pomodoros.",
    placement: "bottom",
  },
  {
    target: '[data-testid="timer-display"]',
    title: "Cronômetro",
    content: "O anel dourado mostra o progresso do ciclo atual e o tempo restante fica bem no centro.",
    placement: "bottom",
  },
  {
    target: '[data-testid="timer-controls"]',
    title: "Controles",
    content: "Reinicie, inicie/pause ou pule para o próximo ciclo com estes três botões.",
    placement: "top",
  },
  {
    target: '[data-testid="toggle-sound"]',
    title: "Som de aviso",
    content: "Ative ou desative o sininho que toca quando um ciclo termina.",
    placement: "left",
  },
  {
    target: '[data-testid="session-count"]',
    title: "Seu progresso",
    content: "Os pontinhos marcam os pomodoros do conjunto atual (até 4) e o texto abaixo conta o total do dia.",
    placement: "top",
  },
  {
    target: '[data-testid="mode-pausa_curta"]',
    title: "Orações nas pausas",
    content: "Durante as pausas, uma oração é sugerida na tela para você elevar o coração a Deus.",
    placement: "bottom",
  },
  {
    title: "Tudo pronto!",
    content:
      'Toque em "Iniciar" para começar seu primeiro pomodoro. Você pode reabrir este tutorial a qualquer momento pelo ícone de ajuda no topo.',
  },
];

// Classic pomodoro durations, in minutes
const DEFAULT_DURATIONS_MIN = {
  estudo: 25,
  pausa_curta: 5,
  pausa_longa: 15,
};

const MIN_DURATION_MINUTES = 1;
const MAX_DURATION_MINUTES = 180;

const MODE_LABELS = {
  estudo: "Estudo",
  pausa_curta: "Pausa curta",
  pausa_longa: "Pausa longa",
};

const clampMinutes = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return DEFAULT_DURATIONS_MIN.estudo;
  return Math.min(MAX_DURATION_MINUTES, Math.max(MIN_DURATION_MINUTES, n));
};

const loadStoredDurationsMin = () => {
  try {
    const raw = localStorage.getItem(DURATIONS_STORAGE_KEY);
    if (!raw) return DEFAULT_DURATIONS_MIN;
    const parsed = JSON.parse(raw);
    return {
      estudo: clampMinutes(parsed.estudo ?? DEFAULT_DURATIONS_MIN.estudo),
      pausa_curta: clampMinutes(parsed.pausa_curta ?? DEFAULT_DURATIONS_MIN.pausa_curta),
      pausa_longa: clampMinutes(parsed.pausa_longa ?? DEFAULT_DURATIONS_MIN.pausa_longa),
    };
  } catch (e) {
    return DEFAULT_DURATIONS_MIN;
  }
};

const formatTime = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

// Simple chime using WebAudio (no asset needed)
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = [880, 660, 880];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.5);
      gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.5 + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.5 + 0.45);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.5);
      osc.stop(now + i * 0.5 + 0.5);
    });
    setTimeout(() => ctx.close(), 2000);
  } catch (e) {
    // ignore
  }
};

const Pomodoro = () => {
  const [durationsMin, setDurationsMin] = useState(loadStoredDurationsMin);
  const durations = useMemo(
    () => ({
      estudo: durationsMin.estudo * 60,
      pausa_curta: durationsMin.pausa_curta * 60,
      pausa_longa: durationsMin.pausa_longa * 60,
    }),
    [durationsMin]
  );

  const [mode, setMode] = useState("estudo");
  const [remaining, setRemaining] = useState(() => loadStoredDurationsMin().estudo * 60);
  const [running, setRunning] = useState(false);
  const [completedCycles, setCompletedCycles] = useState(0); // study pomodoros completed in current set (0..4)
  const [totalSessions, setTotalSessions] = useState(0); // total pomodoros completed
  const [selectedPrayer, setSelectedPrayer] = useState("ave_maria");
  const [soundOn, setSoundOn] = useState(true);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftDurationsMin, setDraftDurationsMin] = useState(durationsMin);

  const intervalRef = useRef(null);
  const endAtRef = useRef(null);

  // Auto-launch the tutorial on first visit
  useEffect(() => {
    let seen = null;
    try {
      seen = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    } catch (e) {
      // ignore storage errors (private mode, etc.)
    }
    if (!seen) {
      const timer = setTimeout(() => setTutorialOpen(true), 700);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  // Persist custom durations
  useEffect(() => {
    try {
      localStorage.setItem(DURATIONS_STORAGE_KEY, JSON.stringify(durationsMin));
    } catch (e) {
      // ignore storage errors (private mode, etc.)
    }
  }, [durationsMin]);

  const isBreak = mode !== "estudo";
  const accent = mode === "estudo" ? "#1C1917" : "#D4AF37";
  const progress = useMemo(() => {
    const total = durations[mode];
    return total > 0 ? (total - remaining) / total : 0;
  }, [mode, remaining, durations]);

  // Tick using wall clock so background tabs stay accurate
  useEffect(() => {
    if (!running) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    endAtRef.current = Date.now() + remaining * 1000;
    intervalRef.current = setInterval(() => {
      const left = Math.max(0, Math.round((endAtRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) {
        clearInterval(intervalRef.current);
        handleComplete();
      }
    }, 250);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  // Update document title with remaining time
  useEffect(() => {
    document.title = `${formatTime(remaining)} · ${MODE_LABELS[mode]} — Pomodoro Cristão`;
  }, [remaining, mode]);

  const switchMode = (next) => {
    setMode(next);
    setRemaining(durations[next]);
    setRunning(false);
  };

  const handleComplete = () => {
    if (soundOn) playChime();
    if (mode === "estudo") {
      const newCompleted = completedCycles + 1;
      setTotalSessions((t) => t + 1);
      // After 4 study cycles -> long break
      if (newCompleted >= 4) {
        setCompletedCycles(0);
        toast("Pomodoro concluído. Tempo de pausa longa.", {
          description: "Aproveite para rezar uma dezena do Terço.",
        });
        setMode("pausa_longa");
        setRemaining(durations.pausa_longa);
      } else {
        setCompletedCycles(newCompleted);
        toast("Pomodoro concluído. Pequena pausa para oração.", {
          description: "Reze com tranquilidade.",
        });
        setMode("pausa_curta");
        setRemaining(durations.pausa_curta);
      }
    } else {
      toast("Pausa encerrada. Vamos retomar o estudo?", {
        description: "Ofereça este próximo ciclo a Deus.",
      });
      setMode("estudo");
      setRemaining(durations.estudo);
    }
    setRunning(false);
  };

  const onStartPause = () => {
    if (remaining <= 0) {
      setRemaining(durations[mode]);
    }
    setRunning((r) => !r);
  };

  const onReset = () => {
    setRunning(false);
    setRemaining(durations[mode]);
  };

  const onSkip = () => {
    setRunning(false);
    handleComplete();
  };

  const openSettings = () => {
    setDraftDurationsMin(durationsMin);
    setSettingsOpen(true);
  };

  const onSaveSettings = () => {
    const next = {
      estudo: clampMinutes(draftDurationsMin.estudo),
      pausa_curta: clampMinutes(draftDurationsMin.pausa_curta),
      pausa_longa: clampMinutes(draftDurationsMin.pausa_longa),
    };
    setDurationsMin(next);
    setRunning(false);
    setRemaining(next[mode] * 60);
    setSettingsOpen(false);
    toast("Durações atualizadas.", {
      description: "Os novos tempos já valem a partir deste ciclo.",
    });
  };

  return (
    <div className="relative min-h-screen min-h-[100svh] w-full overflow-x-hidden bg-[#FDFBF7] text-stone-900">
      {/* Subtle background texture */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-[0.06] mix-blend-multiply"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxsaWdodCUyMG1pbmltYWxpc3QlMjBhYnN0cmFjdCUyMHRleHR1cmUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MjM4OTQwMXww&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Warm gradient wash */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            "radial-gradient(1200px 600px at 50% -10%, rgba(212,175,55,0.08), transparent 60%)",
        }}
      />

      {/* Top bar */}
      <header className="relative z-10 mx-auto flex max-w-3xl items-center justify-between px-4 pt-[max(1.25rem,env(safe-area-inset-top))] sm:px-6 sm:pt-6">
        <div className="flex items-center gap-2.5" data-testid="brand">
          <svg className="h-[18px] w-[18px] shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M11 2h2v6h6v2h-6v12h-2V10H5V8h6V2z"
              fill="#1C1917"
            />
          </svg>
          <span className="whitespace-nowrap font-[Outfit] text-xs uppercase tracking-[0.14em] text-stone-700 sm:text-sm sm:tracking-[0.22em]">
            Pomodoro Cristão
          </span>
        </div>

        <div className="-mr-1 flex shrink-0 items-center sm:mr-0 sm:gap-1">
          <button
            type="button"
            onClick={openSettings}
            data-testid="settings-button"
            aria-label="Ajustar duração dos ciclos"
            className="flex h-10 w-10 items-center justify-center rounded-full text-stone-600 transition-colors hover:bg-stone-900/5 sm:h-11 sm:w-11"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setTutorialOpen(true)}
            data-testid="tutorial-help-button"
            aria-label="Abrir tutorial"
            className="flex h-10 w-10 items-center justify-center rounded-full text-stone-600 transition-colors hover:bg-stone-900/5 sm:h-11 sm:w-11"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setSoundOn((s) => !s)}
            data-testid="toggle-sound"
            aria-label={soundOn ? "Desativar som" : "Ativar som"}
            className="flex h-10 w-10 items-center justify-center rounded-full text-stone-600 transition-colors hover:bg-stone-900/5 sm:h-11 sm:w-11"
          >
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-6 sm:px-6 sm:pb-8 sm:pt-8" data-testid="pomodoro-page">
        {/* Mode tabs */}
        <div className="flex justify-center" data-testid="mode-tabs">
          <div className="flex w-full max-w-[380px] items-center gap-1 rounded-full border border-stone-900/5 bg-white/70 p-1 shadow-[0_2px_10px_rgba(28,25,23,0.04)] backdrop-blur sm:inline-flex sm:w-auto sm:max-w-none">
            {Object.keys(durations).map((m) => {
              const active = mode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => switchMode(m)}
                  data-testid={`mode-${m}`}
                  className={[
                    "min-w-0 flex-1 whitespace-nowrap rounded-full px-2.5 py-2 font-[Outfit] text-xs tracking-wide transition-all sm:flex-none sm:px-5 sm:py-1.5 sm:text-sm",
                    active
                      ? "bg-stone-900 text-stone-50 shadow-sm"
                      : "text-stone-600 hover:text-stone-900",
                  ].join(" ")}
                >
                  {MODE_LABELS[m]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Timer */}
        <div className="mt-6 sm:mt-8 flex flex-col items-center">
          <p
            className="font-[Outfit] text-[11px] sm:text-xs tracking-[0.28em] uppercase text-stone-500"
            data-testid="mode-label"
          >
            {isBreak ? "Pausa para oração" : "Foco no estudo"}
          </p>

          <div className="relative mt-4 flex aspect-square w-[min(72vw,230px)] items-center justify-center sm:w-[280px]">
            <PomodoroRing progress={progress} accent={accent} size={280} />
            <div className="flex flex-col items-center">
              <span
                className="tabular-nums font-[Outfit] text-[clamp(2.85rem,14vw,3.75rem)] tracking-tighter text-stone-900 sm:text-6xl md:text-7xl"
                data-testid="timer-display"
              >
                {formatTime(remaining)}
              </span>
              <span className="mt-1 max-w-[220px] text-center font-[Cormorant_Garamond] text-base italic leading-snug text-stone-500 sm:mt-2 sm:max-w-none sm:text-lg">
                {isBreak
                  ? completedCycles === 0 && mode === "pausa_longa"
                    ? "Reze uma dezena do Terço"
                    : "Eleve o coração a Deus"
                  : "Ofereça este tempo a Deus"}
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-6 flex w-full max-w-[330px] items-center justify-between gap-2 sm:w-auto sm:max-w-none sm:justify-center sm:gap-3" data-testid="timer-controls">
            <Button
              type="button"
              onClick={onReset}
              variant="ghost"
              className="h-12 w-12 shrink-0 rounded-full p-0 text-stone-600 hover:bg-stone-900/5"
              data-testid="timer-reset"
              aria-label="Reiniciar"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>

            <Button
              type="button"
              onClick={onStartPause}
              data-testid="timer-toggle"
              className="h-[52px] min-w-[164px] rounded-full bg-stone-900 px-8 py-0 font-[Outfit] text-sm tracking-wide text-stone-50 shadow-[0_8px_24px_rgba(28,25,23,0.15)] hover:bg-stone-800 sm:h-12 sm:px-9"
            >
              {running ? (
                <>
                  <Pause className="w-4 h-4 mr-2" /> Pausar
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" /> Iniciar
                </>
              )}
            </Button>

            <Button
              type="button"
              onClick={onSkip}
              variant="ghost"
              className="h-12 w-12 shrink-0 rounded-full p-0 text-stone-600 hover:bg-stone-900/5"
              data-testid="timer-skip"
              aria-label="Pular ciclo"
            >
              <SkipForward className="w-5 h-5" />
            </Button>
          </div>

          {/* Cycle indicator */}
          <div className="mt-6 flex flex-col items-center gap-2">
            <CycleDots completed={completedCycles} total={4} />
            <p className="font-[Outfit] text-xs tracking-wide text-stone-500 sm:tracking-wider" data-testid="session-count">
              {totalSessions} {totalSessions === 1 ? "pomodoro concluído" : "pomodoros concluídos"} hoje
            </p>
          </div>
        </div>

        {/* Prayer panel (only during breaks) */}
        <AnimatePresence>
          {isBreak && (
            <motion.section
              key="prayer-section"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="mt-8"
              data-testid="prayer-section"
            >
              <PrayerCard
                selectedId={selectedPrayer}
                onSelect={setSelectedPrayer}
              />
            </motion.section>
          )}
        </AnimatePresence>

        {/* Footer */}
        <footer className="mx-auto mt-10 max-w-md text-center">
          <p className="font-[Cormorant_Garamond] text-base italic leading-relaxed text-stone-500 sm:text-lg" data-testid="footer-quote">
            “Tudo o que fizerdes, fazei-o de coração, como para o Senhor.” — Cl 3,23
          </p>
        </footer>
      </main>

      <OnboardingTour
        steps={TUTORIAL_STEPS}
        open={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        storageKey={TUTORIAL_STORAGE_KEY}
      />

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="settings-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Duração dos ciclos</DialogTitle>
            <DialogDescription>
              Escolha, em minutos, quanto tempo dura cada etapa do seu Pomodoro.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="duration-estudo">Estudo</Label>
              <Input
                id="duration-estudo"
                type="number"
                min={MIN_DURATION_MINUTES}
                max={MAX_DURATION_MINUTES}
                data-testid="duration-input-estudo"
                value={draftDurationsMin.estudo}
                onChange={(e) =>
                  setDraftDurationsMin((d) => ({ ...d, estudo: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="duration-pausa-curta">Pausa curta</Label>
              <Input
                id="duration-pausa-curta"
                type="number"
                min={MIN_DURATION_MINUTES}
                max={MAX_DURATION_MINUTES}
                data-testid="duration-input-pausa_curta"
                value={draftDurationsMin.pausa_curta}
                onChange={(e) =>
                  setDraftDurationsMin((d) => ({ ...d, pausa_curta: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="duration-pausa-longa">Pausa longa</Label>
              <Input
                id="duration-pausa-longa"
                type="number"
                min={MIN_DURATION_MINUTES}
                max={MAX_DURATION_MINUTES}
                data-testid="duration-input-pausa_longa"
                value={draftDurationsMin.pausa_longa}
                onChange={(e) =>
                  setDraftDurationsMin((d) => ({ ...d, pausa_longa: e.target.value }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setSettingsOpen(false)}
              data-testid="settings-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={onSaveSettings}
              data-testid="settings-save"
              className="bg-stone-900 hover:bg-stone-800 text-stone-50"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pomodoro;
